

// ============================================================================
// FILE 2: Leaderboard.js
// src/pages/LMS/Gamification/Leaderboard.js
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Avatar, Chip,
  FormControl, InputLabel, Select, MenuItem, Stack,
  TextField, InputAdornment, Button, Card, CardContent,
  Grid, LinearProgress
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ArrowBack as BackIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../../utils/axiosInstance';

const Leaderboard = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();

  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all_time');
  const [department, setDepartment] = useState('');
  const [limit, setLimit] = useState(50);

  useEffect(() => {
    loadLeaderboard();
  }, [period, department, limit]);

  const loadLeaderboard = async () => {
    setLoading(true);
    try {
      const params = { period, limit };
      if (department) params.department = department;

      const response = await axiosInstance.get('/api/lms/leaderboard', { params });
      setLeaderboard(response.data.data);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      await axiosInstance.post('/api/lms/leaderboard/refresh');
      loadLeaderboard();
    } catch (error) {
      console.error('Error refreshing leaderboard:', error);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return rank;
  };

  const getRankColor = (rank) => {
    if (rank === 1) return 'gold';
    if (rank === 2) return 'silver';
    if (rank === 3) return '#CD7F32';
    return 'inherit';
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(`/chatbot/${chatbotId}/lms`)}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              <TrophyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Leaderboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Top performing students
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              label="Period"
            >
              <MenuItem value="all_time">All Time</MenuItem>
              <MenuItem value="this_month">This Month</MenuItem>
              <MenuItem value="this_week">This Week</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            placeholder="Filter by department..."
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
            sx={{ flexGrow: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Show Top</InputLabel>
            <Select
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              label="Show Top"
            >
              <MenuItem value={10}>Top 10</MenuItem>
              <MenuItem value={25}>Top 25</MenuItem>
              <MenuItem value={50}>Top 50</MenuItem>
              <MenuItem value={100}>Top 100</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* Top 3 Podium */}
      {leaderboard.length >= 3 && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* 2nd Place */}
          <Grid item xs={12} md={4}>
            <Card sx={{ textAlign: 'center', pt: 5 }}>
              <CardContent>
                <Typography variant="h3" sx={{ color: 'silver', mb: 2 }}>
                  🥈
                </Typography>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 2,
                    bgcolor: 'silver',
                    fontSize: '2rem'
                  }}
                >
                  {leaderboard[1]?.student_name?.[0] || 'S'}
                </Avatar>
                <Typography variant="h6" fontWeight={700}>
                  {leaderboard[1]?.student_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {leaderboard[1]?.department}
                </Typography>
                <Chip
                  label={`${leaderboard[1]?.total_points} points`}
                  color="default"
                  sx={{ mt: 2 }}
                />
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Level {leaderboard[1]?.current_level} • {leaderboard[1]?.courses_completed} Courses
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* 1st Place */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                textAlign: 'center',
                pt: 3,
                border: '2px solid gold',
                boxShadow: 4
              }}
            >
              <CardContent>
                <Typography variant="h2" sx={{ color: 'gold', mb: 2 }}>
                  🥇
                </Typography>
                <Avatar
                  sx={{
                    width: 100,
                    height: 100,
                    mx: 'auto',
                    mb: 2,
                    bgcolor: 'gold',
                    fontSize: '2.5rem',
                    border: '3px solid gold'
                  }}
                >
                  {leaderboard[0]?.student_name?.[0] || 'S'}
                </Avatar>
                <Typography variant="h5" fontWeight={700}>
                  {leaderboard[0]?.student_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {leaderboard[0]?.department}
                </Typography>
                <Chip
                  label={`${leaderboard[0]?.total_points} points`}
                  color="warning"
                  sx={{ mt: 2 }}
                />
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Level {leaderboard[0]?.current_level} • {leaderboard[0]?.courses_completed} Courses
                  </Typography>
                </Box>
                <Box sx={{ mt: 2 }}>
                  {[...Array(leaderboard[0]?.badges_count || 0)].map((_, i) => (
                    <StarIcon key={i} sx={{ color: 'gold', fontSize: 20 }} />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* 3rd Place */}
          <Grid item xs={12} md={4}>
            <Card sx={{ textAlign: 'center', pt: 5 }}>
              <CardContent>
                <Typography variant="h3" sx={{ color: '#CD7F32', mb: 2 }}>
                  🥉
                </Typography>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 2,
                    bgcolor: '#CD7F32',
                    fontSize: '2rem'
                  }}
                >
                  {leaderboard[2]?.student_name?.[0] || 'S'}
                </Avatar>
                <Typography variant="h6" fontWeight={700}>
                  {leaderboard[2]?.student_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {leaderboard[2]?.department}
                </Typography>
                <Chip
                  label={`${leaderboard[2]?.total_points} points`}
                  color="default"
                  sx={{ mt: 2 }}
                />
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Level {leaderboard[2]?.current_level} • {leaderboard[2]?.courses_completed} Courses
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Full Leaderboard Table */}
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 700 }}>
          <TableHead>
            <TableRow>
              <TableCell width={80}>Rank</TableCell>
              <TableCell>Student</TableCell>
              <TableCell>Department</TableCell>
              <TableCell align="right">Points</TableCell>
              <TableCell align="center">Level</TableCell>
              <TableCell align="right">Courses</TableCell>
              <TableCell align="center">Badges</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leaderboard.map((student) => (
              <TableRow
                key={student.student_id}
                hover
                sx={{
                  bgcolor: student.rank <= 3 ? 'action.hover' : 'inherit'
                }}
              >
                <TableCell>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ color: getRankColor(student.rank) }}
                  >
                    {getRankIcon(student.rank)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                      {student.student_name?.[0] || 'S'}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {student.student_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {student.email}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>{student.department || '-'}</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600}>
                    {student.total_points}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={`Level ${student.current_level}`}
                    size="small"
                    color="primary"
                  />
                </TableCell>
                <TableCell align="right">{student.courses_completed}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={student.badges_count}
                    size="small"
                    icon={<StarIcon />}
                    color="warning"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Leaderboard;